import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import Image from 'next/image';
import Link from 'next/link';

// Utils
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useMediaQuery } from 'react-responsive';
import { useAppContext } from '../../../../src/store/AppContext';
import packageUtils from '../../../../src/utils/package/packageUtils';
import collapsiblePanel from '../../../../src/utils/collapsiblePanel';
import getPackageDetailsRoute from '../../../../src/utils/package/getPackageDetailsRoute';
import getLanguageId from '../../../../src/utils/i18n/getLanguageId';
import sanitizeHtml from '../../../../src/utils/wysiwyg/sanitizeHtml';
import cn from '../../../../src/utils/cn';
import lockBodyScroll from '../../../../src/utils/lockBodyScroll';
import useImage from '../../../../src/hooks/useImage';

// Components
import Button from '../../../atoms/Button/Button';
import Offers from './Offers/Offers';
import Flag from '../../../atoms/Flag/Flag';
import SelectedPeriods from './SelectedPeriods/SelectedPeriods';
import WarningFixedOptions from './WarningFixedOptions/WarningFixedOptions';
import Ribbon from '../../../molecules/Ribbon/Ribbon';

import styles from './PackageCard.module.scss';

function PackageCard({
  pack, formats, listWasUpdated, firstSelectionDone, setFirstSelectionDone, allExtras, camps,
}) {
  const { t } = useTranslation();
  const { locale } = useRouter();
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const [{ currentPlayer, search }, stateDispatch] = useAppContext();
  const { getImageSrc } = useImage();

  const [offersPanelIsOpened, setOffersPanelIsOpened] = useState(false);
  const offersPanel = useRef();

  useEffect(() => {
    collapsiblePanel(offersPanel, offersPanelIsOpened, isMobile, styles);

    stateDispatch({ type: 'SET_IS_HEADER_FOLDED', payload: offersPanelIsOpened && isMobile });
    stateDispatch({ type: 'SET_IS_BOOKING_NEXT_FOLDED', payload: offersPanelIsOpened && isMobile });
    lockBodyScroll(offersPanelIsOpened && isMobile);
  }, [offersPanelIsOpened, isMobile]);

  const calcPanelHeight = () => {
    if (!isMobile) {
      const panel = offersPanel?.current;
      if (panel) {
        panel.style.maxHeight = `${panel.scrollHeight}px`;
      }
    }
  };

  const allSoldOut = useCallback((list) => list.every((item) => item.flag === 'SOLD_OUT'), []);

  useEffect(() => {
    // if an offer is selected, close all other offers panels
    if (currentPlayer?.stage && currentPlayer?.stage?.packageId !== pack.id) setOffersPanelIsOpened(false);
  }, [currentPlayer?.stage]);

  const handleAddClick = () => {
    setOffersPanelIsOpened(true);
    setFirstSelectionDone(true);

    // scroll the panel into view
    setTimeout(() => {
      if (!isMobile && offersPanel?.current) {
        offersPanel.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 10);
  };

  const getFeatures = useCallback(() => (
    <div className="p-r-20 mobile-p-r-0">
      {pack.features?.length ? (
        <ul className={`${styles.bullet_list} bullet_list`}>
          {pack.features.map((feature, i) => (
            <li key={i} className={`fw-500 fs-12 ${!feature.included ? 'excluded' : ''}`}>
              {feature.name[getLanguageId(locale)]}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  ), [pack, locale]);

  const getPeriods = useCallback(() => (
    <div id={`pack${pack.id}bloc1`} className={cn(['flex-auto mobile-p-r-20'])}>
      {pack.offers?.length || pack.otherOffers?.length ? (
        <div className="fs-20 fw-700 mobile-fs-16 flex a-end j-end mobile-wrap mobile-j-start ws-nowrap">
          <span className="fw-400 fs-18 mobile-fs-14 m-r-5 m-b-1">
            {t('package.priceFrom')}
          </span>
          <div className="flex a-center">
            {`${packageUtils.getLowestOfferPrice(pack.offers?.length ? pack.offers : pack.otherOffers)} €`}
            <WarningFixedOptions pack={pack} allExtras={allExtras} locale={locale} />
          </div>
        </div>
      ) : null}
      {pack.offers?.length ? (
        <ul className="m-t-10 mobile-m-t-5">
          <SelectedPeriods
            pack={pack}
            offers={pack.offers}
            allSoldOut={allSoldOut}
          />
        </ul>
      ) : null}
    </div>
  ), [pack, allExtras, locale, allSoldOut]);

  return (
    pack && Object.values(pack).length ? (
      <section className={`${styles.card_wrapper} m-b-20`}>
        <div className={`${styles.card} flex mobile-d-col a-stretch ${offersPanelIsOpened ? styles.panel_opened : ''}`}>
          <Link href={getPackageDetailsRoute(currentPlayer, search, pack.id)} passHref>
            <div
              className={cn([
                styles.card_img,
                offersPanelIsOpened ? styles.panel_opened : '',
                'c-pointer',
                allSoldOut(pack.offers) ? styles.sold_out_img : '',
              ])}
            >
              <Image
                loader={(img) => img?.src}
                unoptimized
                src={getImageSrc({
                  fileId: pack.thumbnailPath,
                  widths: {
                    default: 305,
                    mobile: 728,
                  },
                })}
                alt={pack.name[getLanguageId(locale)]}
                objectFit="cover"
                layout="fill"
              />
              {pack.new ? (
                <Ribbon label={t('package.new')} color="orange" />
              ) : null}

              {allSoldOut(pack.offers) ? (
                <div className={styles.sold_out_layer}>
                  <div className={cn([styles.sold_out_layer__text, 'fs-14 fw-400'])}>
                    {t('booking.choice.soldout')}
                  </div>
                </div>
              ) : null}
            </div>
          </Link>

          <div className="p-30 w-100 mobile-p-0">
            <div className={`${styles.card_head} flex j-spb mobile-d-col`}>
              <div className="p-r-30 m-t-2 flex-auto flex d-col mobile-m-t-0 mobile-p-r-0 mobile-d-row mobile-w-100">
                <Link href={getPackageDetailsRoute(currentPlayer, search, pack.id)} passHref>
                  <h2 className="text-c-primary fw-700 fs-20 mobile-fs-18 mobile-p-r-20 c-pointer">
                    {pack.name[getLanguageId(locale)]}
                  </h2>
                </Link>
                <h3 className="m-t-15 mobile-m-t-0 mobile-m-l-auto">
                  <Flag color="primary">
                    {Object.values(formats).find((format) => format.id === pack.formatId).name[getLanguageId(locale)]}
                  </Flag>
                </h3>
              </div>

              {isMobile ? null : getPeriods()}
            </div>
            <div className={styles.card_description}>
              {pack.shortDescription?.[getLanguageId(locale)] ? (
                <div
                  className="fw-400 wysiwyg black light-weight fs-14 bullet_list"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(pack.shortDescription[getLanguageId(locale)]),
                  }}
                />
              ) : null}

              <div className="flex">
                <Link href={getPackageDetailsRoute(currentPlayer, search, pack.id)} passHref>
                  <a
                    className={cn([
                      'd-block p-t-5 p-b-5 mobile-m-b-20 underline text-c-secondary fs-14 fw-700 mobile-fw-500',
                    ])}
                  >
                    {t('common.knowMore')}
                  </a>
                </Link>
              </div>

              <div
                className={cn([
                  styles.mobile_actions,
                  'flex j-spb a-end mobile-a-end',
                ])}
              >
                {isMobile ? getPeriods() : getFeatures()}

                <div>
                  <Button
                    text="uppercase"
                    classNames={`${styles.add_btn} ${allSoldOut(pack.offers) ? styles.sold_out : ''}`}
                    onClick={handleAddClick}
                    disabled={
                      (offersPanelIsOpened && !isMobile)
                      || (currentPlayer?.stage && currentPlayer?.stage?.packageId !== pack.id && firstSelectionDone)
                    }
                  >
                    {allSoldOut(pack.offers)
                      ? (isMobile ? t('package.offers.otherDates') : t('package.offers.viewOtherDates'))
                      : t('common.add')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div ref={offersPanel} className={cn([styles.offers, 'flex d-col a-stretch'])}>
          <Offers
            pack={pack}
            offersPanelIsOpened={offersPanelIsOpened}
            setOffersPanelIsOpened={setOffersPanelIsOpened}
            calcPanelHeight={calcPanelHeight}
            listWasUpdated={listWasUpdated}
            isInsidePanel
            setFirstSelectionDone={setFirstSelectionDone}
            camps={camps}
          />
        </div>
      </section>
    ) : null
  );
}

PackageCard.propTypes = {
  pack: PropTypes.shape({}),
  formats: PropTypes.shape({}),
  listWasUpdated: PropTypes.number.isRequired,
  allExtras: PropTypes.shape({}),
};

PackageCard.defaultProps = {
  pack: null,
  formats: [],
  allExtras: null,
};

export default PackageCard;
